import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';

const BUCKET = 'org-assets';
const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

interface UseUploadLogoResult {
    uploading: boolean;
    error: string | null;
    upload: (
        file: File,
        context: { organizationId: string; generatorId: string }
    ) => Promise<string>;
}

/**
 * Upload un logo dans le bucket `org-assets` et retourne son URL publique.
 * Chemin : {organizationId}/generators/{generatorId}/logo-{timestamp}.{ext}
 * - Les RLS Storage vérifient que l'user est membre de l'org.
 */
export function useUploadLogo(): UseUploadLogoResult {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const upload = useCallback(
        async (
            file: File,
            { organizationId, generatorId }: { organizationId: string; generatorId: string }
        ): Promise<string> => {
            setError(null);

            if (!ALLOWED_TYPES.includes(file.type)) {
                const msg = 'Format non supporté. Utilise PNG, JPG, WebP ou SVG.';
                setError(msg);
                throw new Error(msg);
            }
            if (file.size > MAX_SIZE) {
                const msg = 'Fichier trop lourd (max 5 MB).';
                setError(msg);
                throw new Error(msg);
            }

            setUploading(true);

            const ext = extensionFromFile(file);
            const path = `${organizationId}/generators/${generatorId}/logo-${Date.now()}.${ext}`;

            const { error: upErr } = await supabase.storage
                .from(BUCKET)
                .upload(path, file, {
                    upsert: false,
                    cacheControl: '3600',
                    contentType: file.type,
                });

            if (upErr) {
                setError(upErr.message);
                setUploading(false);
                throw upErr;
            }

            const { data: publicData } = supabase.storage.from(BUCKET).getPublicUrl(path);
            setUploading(false);
            return publicData.publicUrl;
        },
        []
    );

    return { uploading, error, upload };
}

function extensionFromFile(file: File): string {
    const byName = file.name.match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase();
    if (byName) return byName;
    const byMime: Record<string, string> = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/webp': 'webp',
        'image/svg+xml': 'svg',
    };
    return byMime[file.type] ?? 'bin';
}
